import { useContext, useEffect, useState } from "react";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwCheckbox from "@components/shared/OwCheckbox/OwCheckbox";
import ContextEnum from "@models/ContextEnum";
import ChipSizeEnum from "@components/shared/OwChips/ChipSizeEnum";
import OwChips from "@components/shared/OwChips/OwChips";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import IQBDDataImportOptions from "@models/QuickBooks Desktop/IQBDDataImportOptions";
import EndpointsEnum from "@models/EndpointsEnum";
import IUpdateSupplierIntegrationsDTO from "@models/DTO/IUpdateSupplierIntegrationsDTO";
import { SocketContext } from "@contexts/SocketContext";
import UiContext from "@contexts/UiContext";

type Props = {
    closeSidebar: () => void;
};

export const QbdDataImportOptionsSidebar = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const defaultImportTypes: IQBDDataImportOptions = {
        ALLOW_IMPORT: {
            CATEGORIES: [],
            Service: true,
            NonInventory: true,
            OtherCharge: true,
            Inventory: true,
            InventoryAssembly: true,
            ItemFixedAsset: true,
            ItemSubTotal: true,
            ItemDiscount: true,
            ItemPayment: true,
            ItemSalesTax: true,
            ItemSalesTaxGroup: true,
            ItemGroup: true,
        },
    };

    const [saveActive, setSaveActive] = useState(false);
    const [importTypes, setImportTypes] = useState<IQBDDataImportOptions>(
        supplierModel.Supplier_Integration.qbd_data_import_options || defaultImportTypes
    );
    const [categoryItems, setCategoryItems] = useState<string[]>([]);
    const [categoryInputValue, setCategoryInputValue] = useState("");

    useEffect(() => {
        socket.on(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onUpdateSupplierIntegrations);

        return () => {
            socket.removeListener(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onUpdateSupplierIntegrations);
        };
    }, []);

    const handleChange = (key: string, e: any) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;

        const updatedImportTypes: IQBDDataImportOptions = {
            ...importTypes,
        };

        // @ts-expect-error
        updatedImportTypes.ALLOW_IMPORT[key] = value;

        setImportTypes(updatedImportTypes);
    };

    const handleCategoryInputChange = (e: any) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        setCategoryInputValue(value);
    };

    const addCategoryItem = () => {
        // const updatedCategoryItems = [
        //     ...categoryItems,
        //     categoryInputValue,
        // ]

        const updatedImportTypes: IQBDDataImportOptions = {
            ...importTypes,
        };

        updatedImportTypes.ALLOW_IMPORT.CATEGORIES = [...updatedImportTypes.ALLOW_IMPORT.CATEGORIES, categoryInputValue];

        setImportTypes(updatedImportTypes);

        // setCategoryItems(updatedCategoryItems);
        setCategoryInputValue("");
    };

    const updateSupplierIntegrations = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            supplierGuid: supplierModel.guid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            // @ts-expect-error
            Supplier_Integration: {
                qbd_data_import_options: importTypes,
            },
        };

        setSaveActive(true);

        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
    };

    const onUpdateSupplierIntegrations = () => {
        setSaveActive(false);
    };

    const updateCategoryItems = (items: string | string[]) => {};

    const checkBoxItems = [
        { label: "noninventory", key: "NonInventory", checked: importTypes.ALLOW_IMPORT.NonInventory },
        { label: "inventory", key: "Inventory", checked: importTypes.ALLOW_IMPORT.Inventory },
        { label: "assembly", key: "Service", checked: importTypes.ALLOW_IMPORT.Service },
        { label: "other_charge", key: "OtherCharge", checked: importTypes.ALLOW_IMPORT.OtherCharge },
        { label: "subtotal", key: "ItemSubTotal", checked: importTypes.ALLOW_IMPORT.ItemSubTotal },
        { label: "discount", key: "ItemDiscount", checked: importTypes.ALLOW_IMPORT.ItemDiscount },
        { label: "item_sales_tax", key: "ItemSalesTax", checked: importTypes.ALLOW_IMPORT.ItemSalesTax },
        { label: "item_sales_tax_group", key: "ItemSalesTaxGroup", checked: importTypes.ALLOW_IMPORT.ItemSalesTaxGroup },
        { label: "item_payment", key: "ItemPayment", checked: importTypes.ALLOW_IMPORT.ItemPayment },
        { label: "item_group", key: "ItemGroup", checked: importTypes.ALLOW_IMPORT.ItemGroup },
        { label: "item_assembly", key: "InventoryAssembly", checked: importTypes.ALLOW_IMPORT.InventoryAssembly },
        { label: "item_fixed_asset", key: "ItemFixedAsset", checked: importTypes.ALLOW_IMPORT.ItemFixedAsset },
    ];

    return (
        <div className={"QbdDataImportOptionsSidebar p-2"}>
            <div className={"d-flex align-items-center justify-content-between"}>
                <div>
                    <OwTitle title={"edit_data_import_options"} tag={"h4"} />
                </div>
                <div>
                    <OwButton className={"bg-danger"} centerIconClass={"fas fa-times"} fn={props.closeSidebar} />
                </div>
            </div>

            <div className="mt-4 d-flex flex-column">
                <OwTitle title={"item_types"} tag={"h5"} />
                {checkBoxItems.map((item, index) => {
                    return (
                        <OwCheckbox
                            key={index}
                            label={item.label}
                            truthyVal={true}
                            falsyVal={false}
                            fn={(e) => handleChange(item.key, e)}
                            checked={item.checked}
                        />
                    );
                })}

                <div className={"mt-3"}>
                    <OwTitle title={"categories"} tag={"h5"} />
                    <div className={"input-group mb-2"}>
                        <OwInputWrapper
                            placeholder={"category"}
                            name={"category"}
                            fn={handleCategoryInputChange}
                            value={categoryInputValue}
                        />
                        <div className={"input-group-append"}>
                            <OwButton title={"add"} fn={addCategoryItem} />
                        </div>
                    </div>

                    <OwChips
                        chips={importTypes.ALLOW_IMPORT.CATEGORIES}
                        updatedFn={updateCategoryItems}
                        context={ContextEnum.PRIMARY}
                        isStacked={true}
                        chipSize={ChipSizeEnum.XS}
                    />
                </div>

                <div>
                    <OwButton title={"save"} fn={updateSupplierIntegrations} disableOnActive={true} active={saveActive} />
                </div>
            </div>
        </div>
    );
};
